// ── Proposal Pages default data & helpers ─────────────────────────────────────
// Revolead Construction — Marketing Template System

import { BRAND_NAME, BRAND_ACN, fetchCompanyDefaults } from './brandConfig';

import { uid } from './uid';

// ── Default Variables ─────────────────────────────────────────────────────────
// S-02: company_address/phone/director are now fetched from backend API
export const DEFAULT_PROPOSAL_VARIABLES = {
  // Company info (our side)
  company_name: BRAND_NAME,
  company_abn: '',
  company_acn: BRAND_ACN,
  company_address: '',
  company_phone: '',
  company_email: '',
  company_director: '',
  company_director_title: 'Director',
  company_website: '',
  company_logo_url: '',

  // Client info (their side)
  client_name: '',
  client_abn: '',
  client_contact_person: '',
  client_contact_title: '',
  client_address: '',
  client_phone: '',
  client_email: '',

  // Project info
  project_name: '',
  project_address: '',
  project_description: '',
  proposal_date: '',
  proposal_revision: 'A',
  proposal_reference: '',

  // Partner logos
  partner_logos: [],
};

// ── Default section data ──────────────────────────────────────────────────────

const DEFAULT_COVER = {
  background_image_url: '',
  background_image_type: 'placeholder',  // 'placeholder' | 'url' | 'local'
  background_image_file: null,
  overlay_opacity: 60,
  title_template: '{{project_name}} / PROPOSAL',
};

const DEFAULT_COVER_LETTER = {
  body: `Thank you for the opportunity to submit our proposal for the {{project_name}} project at {{project_address}}.

We are pleased to present this proposal detailing our understanding of your requirements, our relevant experience, and our approach to delivering exceptional results for {{client_name}}.

Our team brings extensive experience in commercial fit-out projects, and we are confident in our ability to meet and exceed your expectations for this project.`,
};

const DEFAULT_ADVANTAGES = [
  {
    id: uid(),
    icon: 'Shield',
    title: 'Proven Track Record',
    description: 'Over years of delivering quality commercial fit-out projects across Australia.',
  },
  {
    id: uid(),
    icon: 'Clock',
    title: 'On-Time Delivery',
    description: 'Committed to meeting project deadlines with meticulous planning and execution.',
  },
  {
    id: uid(),
    icon: 'Award',
    title: 'Quality Assurance',
    description: 'Rigorous quality control processes ensuring the highest standards of workmanship.',
  },
  {
    id: uid(),
    icon: 'Users',
    title: 'Experienced Team',
    description: 'A dedicated team of professionals with expertise in commercial construction.',
  },
];

const DEFAULT_SELECTED_PROJECTS_INTRO = {
  title: 'SELECTED PROJECTS',
  subtitle: 'A showcase of our recent commercial fit-out projects demonstrating our capability and commitment to excellence.',
};

const DEFAULT_PROJECT_CASES = [
  {
    id: uid(),
    project_name: '',
    client_name: '',
    description: '',
    highlights: [],
    images: [],
    partner_logos: [],
  },
];

const DEFAULT_PROJECT_ALIGNMENT = {
  intro: 'Our experience aligns closely with the requirements of this project:',
  points: [],
  summary: '',
};

const DEFAULT_DELIVERY_APPROACH = {
  title: 'DELIVERY APPROACH',
  steps: [
    { id: uid(), step_number: 1, title: 'Design & Planning', description: 'Comprehensive design development and planning phase.' },
    { id: uid(), step_number: 2, title: 'Procurement', description: 'Sourcing and procurement of materials and subcontractors.' },
    { id: uid(), step_number: 3, title: 'Construction', description: 'Execution of fit-out works with quality oversight.' },
    { id: uid(), step_number: 4, title: 'Completion & Handover', description: 'Final inspections, defect rectification, and project handover.' },
  ],
};

const DEFAULT_WORK_SCHEDULE = {
  start_date: '',
  end_date: '',
  milestones: [],
};

const DEFAULT_RESOURCES_SUMMARY = {
  team: [
    { id: uid(), name: '', role: 'Director', description: 'Overall project leadership and client management', avatar_url: '' },
  ],
  key_metrics: [],
  summary: '',
};

// ── Get default proposal data ─────────────────────────────────────────────────
export function getDefaultProposalData() {
  return {
    id: null,
    proposal_no: '',
    quote_id: '',
    quote_no: '',
    created_at: '',
    updated_at: '',

    // Unified variables
    variables: JSON.parse(JSON.stringify(DEFAULT_PROPOSAL_VARIABLES)),

    // Section content
    cover: JSON.parse(JSON.stringify(DEFAULT_COVER)),
    cover_letter: JSON.parse(JSON.stringify(DEFAULT_COVER_LETTER)),
    advantages: JSON.parse(JSON.stringify(DEFAULT_ADVANTAGES)),
    selected_projects_intro: JSON.parse(JSON.stringify(DEFAULT_SELECTED_PROJECTS_INTRO)),
    project_cases: JSON.parse(JSON.stringify(DEFAULT_PROJECT_CASES)),
    project_alignment: JSON.parse(JSON.stringify(DEFAULT_PROJECT_ALIGNMENT)),
    delivery_approach: JSON.parse(JSON.stringify(DEFAULT_DELIVERY_APPROACH)),
    work_schedule: JSON.parse(JSON.stringify(DEFAULT_WORK_SCHEDULE)),
    resources_summary: JSON.parse(JSON.stringify(DEFAULT_RESOURCES_SUMMARY)),

    // Partner logos (shared)
    partner_logos: [],
  };
}

// ── Variable replacement helper ───────────────────────────────────────────────
export function replaceVariables(text, variables) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return variables[key] !== undefined ? variables[key] : match;
  });
}

// ── Map quote data → proposal variables ─────────────────────────────────────
// This is the single source of truth for the quote → proposal variable mapping.
// Used by both createProposalFromQuote() and the backend sync API.
// IMPORTANT: We sync ALL mapped fields including empty strings to ensure
// the proposal always reflects the exact state of the quote.
export function mapQuoteToProposalVariables(quoteData) {
  const vars = {};

  // Client info — always set, even if empty (ensures full sync)
  vars.client_name = quoteData.client_name ?? '';
  vars.client_abn = quoteData.client_abn ?? '';
  vars.client_contact_person = quoteData.contact_person ?? '';
  vars.client_address = quoteData.client_address ?? '';

  // Project info
  vars.project_name = quoteData.project_name ?? '';
  vars.project_address = quoteData.project_address ?? '';
  vars.proposal_revision = quoteData.revision ?? '';
  vars.proposal_reference = quoteData.quote_no ?? '';

  // Company info (nested in quote)
  const ci = quoteData.company_info || {};
  vars.company_name = ci.name ?? '';
  vars.company_acn = ci.acn ?? '';
  vars.company_address = ci.address ?? '';
  vars.company_phone = ci.phone ?? '';
  vars.company_director = ci.director ?? '';
  vars.company_logo_url = ci.logo_url ?? '';

  return vars;
}

// ── Map proposal variables → quote fields ─────────────────────────────────────
// Reverse mapping: proposal variables back to quote's top-level + nested fields.
// IMPORTANT: We sync ALL mapped fields including empty strings for full consistency.
export function mapProposalVariablesToQuote(variables) {
  const updates = {};
  const ci = {};

  updates.client_name = variables.client_name ?? '';
  updates.client_abn = variables.client_abn ?? '';
  updates.contact_person = variables.client_contact_person ?? '';
  updates.client_address = variables.client_address ?? '';
  updates.project_name = variables.project_name ?? '';
  updates.project_address = variables.project_address ?? '';
  updates.revision = variables.proposal_revision ?? '';

  ci.name = variables.company_name ?? '';
  ci.acn = variables.company_acn ?? '';
  ci.address = variables.company_address ?? '';
  ci.phone = variables.company_phone ?? '';
  ci.director = variables.company_director ?? '';
  ci.logo_url = variables.company_logo_url ?? '';

  updates.company_info = ci;

  return updates;
}

// ── Create proposal from quote ────────────────────────────────────────────────
export function createProposalFromQuote(quoteData) {
  const proposal = getDefaultProposalData();
  proposal.quote_id = quoteData.id || '';
  proposal.quote_no = quoteData.quote_no || '';

  // Sync all variables from quote using the shared mapping
  const mapped = mapQuoteToProposalVariables(quoteData);
  Object.assign(proposal.variables, mapped);

  // Set date after mapping so it always wins
  proposal.variables.proposal_date = new Date().toISOString().slice(0, 10);

  return proposal;
}
